'use client';

import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  forgotPasswordSchema,
  type ForgotPasswordFormData,
} from '@/schemas/forgotPasswordSchema';
import { userService } from '@/api/user/userApi';
import { handleGlobalError } from '@/utils/error-handler';

const ForgotPassword: React.FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await userService.requestPasswordReset(data.email);
      setSentEmail(data.email);
      setEmailSent(true);
      toast({
        title: 'Correo enviado',
        description:
          'Te hemos enviado las instrucciones para recuperar tu contraseña. Revisa tu bandeja de entrada y también la carpeta de spam.',
      });
    } catch (error) {
      console.error('Password reset request error:', error);
      handleGlobalError(
        error,
        'Error',
        'No pudimos enviar el correo. Verifica que el email sea correcto.',
      );
    }
  };

  if (emailSent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-primary">
        <Card className="w-full max-w-md bg-white shadow-lg">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <img
                src="/assets/images/logo.svg"
                alt="GetMatch Logo"
                className="mx-auto mb-4 h-20 w-auto"
              />
              <h2 className="font-gilroy text-2xl font-bold text-primary">
                Correo Enviado
              </h2>
            </div>

            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <p className="font-opensans text-sm leading-relaxed text-gray-700">
                  Te hemos enviado un correo a{' '}
                  <span className="font-semibold text-secondary">
                    {sentEmail}
                  </span>{' '}
                  con las instrucciones para recuperar tu contraseña.
                </p>
                <p className="mt-3 font-opensans text-sm text-gray-600">
                  Revisa tu bandeja de entrada y sigue las instrucciones del
                  correo.
                </p>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center font-opensans text-sm text-primary transition-colors hover:text-secondary"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Volver al inicio de sesión
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary">
      <Card className="w-full max-w-md bg-white shadow-lg">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <img
              src="/assets/images/logo.svg"
              alt="GetMatch Logo"
              className="mx-auto mb-4 h-20 w-auto"
            />
            <h2 className="font-gilroy text-2xl font-bold text-primary">
              Recuperar Contraseña
            </h2>
            <p className="mt-2 font-opensans text-sm text-gray-600">
              Te enviaremos un mail al correo electrónico ingresado para que
              puedas recuperar tu contraseña.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="font-opensans text-sm font-semibold text-primary"
              >
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                className="border-accent focus:border-secondary focus:ring-secondary"
                disabled={isSubmitting}
                placeholder="tu@email.com"
              />
              {errors.email && (
                <p className="font-opensans text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded bg-secondary py-3 font-gilroy font-bold text-white transition duration-200 hover:bg-secondary/90"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Enviar correo
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center font-opensans text-sm text-primary transition-colors hover:text-secondary"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Volver al inicio de sesión
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
